import React, { useState, useRef, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Product, IntegrationField, ProductFormData } from '../../types';
import { 
  PlusCircle, 
  ImagePlus, 
  X, 
  Loader2, 
  ListPlus, 
  Code, 
  ChevronDown, 
  ChevronUp,
  Key,
  Database,
  Link as LinkIcon,
  Lock,
  Trash2,
  Info,
  ArrowRight,
  Plus
} from 'lucide-react';
import IntegrationFieldForm from './IntegrationFieldForm';
import IntegrationFieldItem from './IntegrationFieldItem';
import ImageUploadModal from './ImageUploadModal';

interface ProductFormProps {
  initialProduct?: Product;
  onSubmit: (data: ProductFormData) => Promise<void>;
  isLoading?: boolean;
}

// Product form validation schema
const productSchema = z.object({
  name: z.string().min(1, 'Product name is required'),
  logo_url: z.string().optional(),
  tags: z.record(z.string(), z.string().optional()).optional(),
});

type ProductFormValues = z.infer<typeof productSchema>;

// Create a section component for collapsible sections
interface SectionProps {
  title: string;
  leftIcon?: React.ReactNode;
  children: React.ReactNode;
  badgeCount?: number;
  badgeColor?: string;
  defaultOpen?: boolean;
  actions?: React.ReactNode;
  id?: string;
  forceOpen?: boolean;
}

const FormSection: React.FC<SectionProps> = ({
  title,
  leftIcon,
  children,
  badgeCount,
  badgeColor = 'bg-primary-700/30 text-primary-300',
  defaultOpen = true,
  actions,
  id,
  forceOpen
}) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  
  useEffect(() => {
    if (forceOpen !== undefined) {
      setIsOpen(forceOpen);
    }
  }, [forceOpen]);

  return (
    <div className="bg-background-hover border border-border-dark rounded-lg mb-6 overflow-hidden" id={id}>
      <div 
        className="flex items-center justify-between p-4 cursor-pointer hover:bg-background-hover/30 transition-colors"
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="flex items-center">
          {leftIcon && <span className="mr-2">{leftIcon}</span>}
          <h3 className="text-lg font-medium">{title}</h3>
          {typeof badgeCount !== 'undefined' && (
            <span className={`ml-2 text-xs px-2 py-0.5 rounded-full ${badgeCount > 0 ? badgeColor : 'bg-background-hover text-text-muted-dark'}`}>
              {badgeCount} {badgeCount === 1 ? 'item' : 'items'}
            </span>
          )}
        </div>
        <div className="flex items-center">
          {actions}
          <button type="button" className="ml-2 p-1">
            {isOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
          </button>
        </div>
      </div>
      
      {isOpen && (
        <div className="p-4 border-t border-border-dark/50">
          {children}
        </div>
      )}
    </div>
  );
};

// Group fields by category for better organization
const groupFieldsByCategory = (fields: IntegrationField[]) => {
  const groups: Record<string, IntegrationField[]> = {
    authentication: [],
    configuration: [],
    mapping: [],
    other: []
  };

  fields.forEach(field => {
    // Simple categorization based on field key or type
    if (field.type === 'soar-mapping') {
      groups.mapping.push(field);
    } else if (field.field_key.includes('key') || field.field_key.includes('token') || field.field_key.includes('secret') || field.type === 'secret') {
      groups.authentication.push(field);
    } else if (field.field_key === 'services' || field.field_key.includes('config') || field.field_key.includes('url')) {
      groups.configuration.push(field);
    } else {
      groups.other.push(field);
    }
  });

  return groups;
};

// Add tooltip component
interface TooltipProps {
  content: string;
}

const Tooltip: React.FC<TooltipProps> = ({ content }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  
  return (
    <div className="relative inline-block ml-1">
      <Info 
        size={14} 
        className="text-gray-400 cursor-help" 
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
      />
      {showTooltip && (
        <div className="absolute z-50 bottom-full left-1/2 transform -translate-x-1/2 -translate-y-1 px-3 py-2 text-xs bg-gray-800 text-gray-200 rounded shadow-lg w-48 text-center">
          {content}
          <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-t-4 border-l-4 border-r-4 border-transparent border-t-gray-800"></div>
        </div>
      )}
    </div>
  );
};

const ProductForm: React.FC<ProductFormProps> = ({
  initialProduct,
  onSubmit,
  isLoading = false,
}) => {
  // Basic form state
  const [showFieldForm, setShowFieldForm] = useState(false);
  const [fieldType, setFieldType] = useState<'regular' | 'services' | 'soar-mapping'>('regular');
  const [editingField, setEditingField] = useState<IntegrationField | undefined>(undefined);
  
  // Use a more direct state management approach for integration fields
  // Store as an object map for easier manipulation
  const [fieldsMap, setFieldsMap] = useState<Record<string, IntegrationField>>(() => {
    const map: Record<string, IntegrationField> = {};
    
    // Populate with initial fields if available
    if (initialProduct?.integration_fields) {
      initialProduct.integration_fields.forEach(field => {
        // Create a deep copy to avoid reference issues
        map[field.field_key] = JSON.parse(JSON.stringify(field));
      });
    }
    
    return map;
  });
  
  // Add product type state
  const [productType, setProductType] = useState<'saas' | 'logger'>(
    initialProduct ? ((initialProduct as any).type || 'saas') : 'saas'
  );
  
  // For logger product form
  const [loggerFormData, setLoggerFormData] = useState({
    product_name: initialProduct?.name || '',
    product_type: initialProduct?.tags?.productType || '',
    log_type: initialProduct?.tags?.logType || '',
    template_name: initialProduct ? ((initialProduct as any).logger_config?.template_name || '') : '',
    queue_size: initialProduct ? ((initialProduct as any).logger_config?.queue_size || '1000') : '1000',
    max_disk_space: initialProduct ? ((initialProduct as any).logger_config?.max_disk_space || '100') : '100',
    worker_threads: initialProduct ? ((initialProduct as any).logger_config?.worker_threads || '4') : '4',
    filename: initialProduct ? ((initialProduct as any).logger_config?.filename || '') : '',
    status: initialProduct ? ((initialProduct as any).logger_config?.status || 'active') : 'active',
    keywords: initialProduct ? ((initialProduct as any).logger_config?.keywords || []) : []
  });
  
  // Derive the array version for UI rendering
  const integrationFields = Object.values(fieldsMap);
  
  // Other state declarations
  const [logoPreview, setLogoPreview] = useState<string | undefined>(initialProduct?.logo_url);
  const [showAddOptions, setShowAddOptions] = useState(false);
  const [tags, setTags] = useState<{key: string, value: string}[]>(
    initialProduct?.tags 
      ? Object.entries(initialProduct.tags).map(([key, value]) => ({ key, value: value || '' })) 
      : [{ key: 'productName', value: '' }, { key: 'productModule', value: '' }]
  );
  const [newTagKey, setNewTagKey] = useState('');
  const [newTagValue, setNewTagValue] = useState('');
  const [showAddTagForm, setShowAddTagForm] = useState(false);
  const [showImageUploadModal, setShowImageUploadModal] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Add grouped fields state
  const [expandedFieldGroups, setExpandedFieldGroups] = useState<Record<string, boolean>>({
    authentication: true,
    configuration: true,
    mapping: true,
    other: true
  });

  // For logger form
  const [newKeyword, setNewKeyword] = useState<string>('');
  const [newCondition, setNewCondition] = useState<'NONE' | 'AND' | 'OR'>('NONE');
  
  // Handle product type change
  const handleProductTypeChange = (type: 'saas' | 'logger') => {
    setProductType(type);
  };
  
  // Handle keyword addition for logger products
  const handleAddKeyword = () => {
    if (newKeyword.trim()) {
      setLoggerFormData({
        ...loggerFormData,
        keywords: [...loggerFormData.keywords, { 
          keyword: newKeyword.trim(), 
          condition: newCondition === 'NONE' ? '' : newCondition 
        }]
      });
      setNewKeyword('');
      setNewCondition('NONE');
    }
  };

  // Handle condition change for keywords
  const handleConditionChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value as 'NONE' | 'AND' | 'OR';
    setNewCondition(value);
    
    // Auto-add keyword when a condition is selected and keyword is not empty
    if (value !== 'NONE' && newKeyword.trim()) {
      handleAddKeyword();
    }
  };

  // Handle keyword input change with auto-add when Enter is pressed
  const handleKeywordInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNewKeyword(e.target.value);
  };

  // Handle Enter key press to add keyword
  const handleKeywordKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && newKeyword.trim()) {
      e.preventDefault();
      handleAddKeyword();
    }
  };

  // Handle keyword removal for logger products
  const handleRemoveKeyword = (index: number) => {
    const updatedKeywords = [...loggerFormData.keywords];
    updatedKeywords.splice(index, 1);
    setLoggerFormData({
      ...loggerFormData,
      keywords: updatedKeywords
    });
  };
  
  // Handle logger form field changes
  const handleLoggerFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setLoggerFormData({
      ...loggerFormData,
      [name]: value
    });
  };

  // Debug logging to show the current state of fields
  useEffect(() => {
    // Current integration fields count
    
    // Check for services field specifically
    const servicesField = fieldsMap['services'];
    if (servicesField) {
      // Services field state checked
    }
  }, [fieldsMap]);

  // Configure form with react-hook-form and zod validation
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm<ProductFormValues>({
    resolver: zodResolver(productSchema),
    defaultValues: {
      name: initialProduct?.name || '',
      logo_url: initialProduct?.logo_url || '',
      tags: {
        productName: initialProduct?.tags?.productName || '',
        productModule: initialProduct?.tags?.productModule || '',
      },
    },
  });

  // Add a field to the fields map
  const addField = (field: IntegrationField) => {
    // Make a deep copy to ensure no reference issues
    const fieldCopy = JSON.parse(JSON.stringify(field));
    
    // Log the field being added
            // Adding field to map
    
    // Update the fields map with the new field
    setFieldsMap(prev => ({
      ...prev,
      [field.field_key]: fieldCopy
    }));
    
    // Log the updated state
            // Field added successfully
  };

  // Update an existing field in the fields map
  const updateField = (field: IntegrationField) => {
    // Make a deep copy to ensure no reference issues
    const fieldCopy = JSON.parse(JSON.stringify(field));
    
    // Log the field being updated
            // Updating field in map
    
    // Update the fields map with the updated field
    setFieldsMap(prev => ({
      ...prev,
      [field.field_key]: fieldCopy
    }));
    
    // Log the updated state
            // Field updated successfully
  };

  // Remove a field from the fields map
  const removeField = (fieldKey: string) => {
    // Log the field being removed
            // Removing field from map
    
    // Create a new map without the removed field
    setFieldsMap(prev => {
      const newMap = { ...prev };
      delete newMap[fieldKey];
      return newMap;
    });
    
    // Log the updated state
            // Field removed successfully
  };

  // Handle logo URL change
  const handleLogoUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const url = e.target.value;
    setValue('logo_url', url);
    setLogoPreview(url);
  };

  // Handle opening the image upload modal
  const handleOpenImageUploadModal = () => {
    setShowImageUploadModal(true);
  };

  // Handle image selection from the modal
  const handleImageSelected = (imageUrl: string) => {
    setLogoPreview(imageUrl);
    setValue('logo_url', imageUrl);
  };

  // Handle field submission from the IntegrationFieldForm
  const handleFieldSubmit = (field: IntegrationField) => {
    // Make sure we have a valid field
    if (!field || !field.field_key || !field.label) {
      console.error("Invalid field submitted:", field);
      return;
    }
    
    try {
      // Log the field being received for debugging
      // Field received for submission
      
      if (editingField) {
        // We're updating an existing field
        updateField(field);
      } else {
        // We're adding a new field
        addField(field);
      }
      
      // Reset editing state and hide form
      setTimeout(() => {
        setEditingField(undefined);
        setShowFieldForm(false);
        setFieldType('regular');
        setShowAddOptions(false);
      }, 100);
    } catch (error) {
      console.error("Error handling field submission:", error);
      // If there's an error, still try to close the form
      setEditingField(undefined);
      setShowFieldForm(false);
      setFieldType('regular');
      setShowAddOptions(false);
    }
  };

  // Add state to track which section should be forced open
  const [openSection, setOpenSection] = useState<string | null>(null);

  // Handle add regular field
  const handleAddRegularField = () => {
    setEditingField(undefined);
    setFieldType('regular');
    setShowFieldForm(true);
    setShowAddOptions(false);
    setOpenSection('integration-fields');
  };

  // Handle add services field
  const handleAddServicesField = () => {
    // Check if services field already exists
    const hasServicesField = 'services' in fieldsMap;
    
    if (!hasServicesField) {
      // Pre-populate with services field defaults
      const servicesField: IntegrationField = {
        field_key: 'services',
        label: 'Services',
        type: 'multi-select',
        is_required: false,
        options: []
      };
      
      setEditingField(servicesField);
      setFieldType('services');
      setShowFieldForm(true);
    }
    
    setShowAddOptions(false);
    setOpenSection('services-fields');
  };

  // Handle add soar field mapping
  const handleAddSoarFieldMapping = () => {
    setEditingField(undefined);
    setFieldType('soar-mapping');
    setShowFieldForm(true);
    setShowAddOptions(false);
    setOpenSection('soar-mapping');
  };

  // Edit integration field
  const handleEditField = (field: IntegrationField) => {
    // Make a deep copy of the field to avoid reference issues
    const fieldCopy = JSON.parse(JSON.stringify(field));
    
    setEditingField(fieldCopy);
    
    // Set field type and open appropriate section
    if (field.field_key === 'services') {
      setFieldType('services');
      setOpenSection('services-fields');
    } else if (field.type === 'soar-mapping') {
      setFieldType('soar-mapping');
      setOpenSection('soar-mapping');
    } else {
      setFieldType('regular');
      setOpenSection('integration-fields');
    }
    
    setShowFieldForm(true);
  };

  // Delete integration field
  const handleDeleteField = (fieldKey: string) => {
    removeField(fieldKey);
  };

  // Cancel field form
  const handleCancelFieldForm = () => {
    setShowFieldForm(false);
    setEditingField(undefined);
    setFieldType('regular');
  };

  // Form submission handler
  const [isSubmitting, setIsSubmitting] = useState(false);
  const submitHandler = (data: ProductFormValues) => {
    // Prevent multiple submissions
    if (isSubmitting) return;
    setIsSubmitting(true);
    
    try {
      // Prepare the product data based on product type
      let productData: ProductFormData;
      
      if (productType === 'saas') {
        // For SaaS products
        // Prepare tags object from array
        const tagsObject: Record<string, string> = {};
        tags.forEach(tag => {
          if (tag.key && tag.value) {
            tagsObject[tag.key] = tag.value;
          }
        });
      
        // Get integration fields as array
        const fieldsArray = Object.values(fieldsMap);
      
        // Prepare the SaaS product data
        productData = {
          name: data.name,
          logo_url: data.logo_url,
          type: 'saas',
          tags: tagsObject,
          integration_fields: fieldsArray
        };
      } 
      else {
        // For Logger products
        // Ensure product name is set - use either form name or logger product name
        const productName = data.name || loggerFormData.product_name;
        
        if (!productName) {
          console.error("Product name is required");
          alert("Product name is required");
          setIsSubmitting(false);
          return;
        }
        
        // Prepare the logger product data
        productData = {
          name: productName,
          logo_url: data.logo_url,
          type: 'logger',
          tags: {
            productType: loggerFormData.product_type,
            logType: loggerFormData.log_type
          },
          integration_fields: [],
          logger_config: {
            template_name: loggerFormData.template_name,
            queue_size: loggerFormData.queue_size,
            max_disk_space: loggerFormData.max_disk_space,
            worker_threads: loggerFormData.worker_threads,
            filename: loggerFormData.filename,
            status: loggerFormData.status,
            keywords: loggerFormData.keywords
          }
        };
      }
      
      // Submit the product data
      onSubmit(productData)
        .finally(() => {
          // Reset submission state after a delay
          setTimeout(() => {
            setIsSubmitting(false);
          }, 500);
        });
    } catch (error) {
      console.error("Error in form submission:", error);
      setIsSubmitting(false);
    }
  };

  // Toggle add options dropdown
  const toggleAddOptions = () => {
    setShowAddOptions(!showAddOptions);
  };

  // Get existing field keys for validation
  const existingFieldKeys = Object.keys(fieldsMap);
  
  // Check if services field already exists
  const hasServicesField = 'services' in fieldsMap;

  // Handle adding a new tag
  const handleAddTag = () => {
    if (!newTagKey.trim()) return;
    
    const tagExists = tags.some(tag => tag.key === newTagKey);
    if (tagExists) return;
    
    setTags([...tags, { key: newTagKey, value: newTagValue }]);
    setNewTagKey('');
    setNewTagValue('');
    setShowAddTagForm(false);
  };
  
  // Handle removing a tag
  const handleRemoveTag = (key: string) => {
    setTags(tags.filter(tag => tag.key !== key));
  };
  
  // Handle updating tag value
  const handleTagValueChange = (key: string, value: string) => {
    setTags(tags.map(tag => tag.key === key ? { ...tag, value } : tag));
  };

  // Toggle field group expansion
  const toggleFieldGroup = (groupName: string) => {
    setExpandedFieldGroups(prev => ({
      ...prev,
      [groupName]: !prev[groupName]
    }));
  };

  // Group integration fields by category
  const fieldGroups = groupFieldsByCategory(integrationFields);
  const regularFieldCount = integrationFields.filter(f => f.type !== 'soar-mapping').length;

  return (
    <div className="card max-w-5xl mx-auto relative z-0">
      <div className="mb-6">
        <h2 className="text-xl font-semibold mb-2">Product Onboarding</h2>
        <p className="text-text-muted-dark">
          Configure a new product integration for your tenants
        </p>
      </div>

      <form onSubmit={handleSubmit(submitHandler)} className="relative">
        {/* Basic Information Section */}
        <FormSection 
          title="Basic Information" 
          leftIcon={<Database size={18} className="text-primary" />}
          defaultOpen={true}
          id="basic-info"
          forceOpen={openSection === 'basic-info'}
        >
          {/* Product Type Selection */}
          <div className="mb-6">
            <label className="form-label">
              Product Type <span className="text-status-danger">*</span>
            </label>
            <div className="flex space-x-4 mt-2">
              <div 
                className={`flex-1 p-3 border rounded-md cursor-pointer flex items-center justify-center transition-colors ${
                  productType === 'saas' 
                    ? 'bg-primary/20 border-primary/50 text-primary-300' 
                    : 'border-border-dark hover:bg-background-hover'
                }`}
                onClick={() => handleProductTypeChange('saas')}
              >
                <span className="font-medium">SaaS</span>
              </div>
              <div 
                className={`flex-1 p-3 border rounded-md cursor-pointer flex items-center justify-center transition-colors ${
                  productType === 'logger' 
                    ? 'bg-green-700/20 border-green-700/50 text-green-300' 
                    : 'border-border-dark hover:bg-background-hover'
                }`}
                onClick={() => handleProductTypeChange('logger')}
              >
                <span className="font-medium">Logger</span>
              </div>
            </div>
            <p className="text-xs text-text-muted-dark mt-1">
              Select the type of product you want to configure
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left column - Basic info */}
            <div className="lg:col-span-1">
              <div className="mb-6">
                <label htmlFor="name" className="form-label">
                  Product Name <span className="text-status-danger">*</span>
                </label>
                <input
                  id="name"
                  type="text"
                  className={`form-input ${errors.name ? 'border-status-danger' : ''}`}
                  placeholder="e.g., Slack"
                  {...register('name')}
                />
                {errors.name && <p className="form-error mt-1">{errors.name.message}</p>}
              </div>

              <div className="mb-6">
                <label htmlFor="logo_url" className="form-label">
                  Product Logo
                </label>
                <div className="flex items-center gap-2 mb-2">
                  <input
                    id="logo_url"
                    type="text"
                    className="form-input flex-grow"
                    placeholder="https://example.com/logo.png"
                    onChange={handleLogoUrlChange}
                    value={watch('logo_url')}
                  />
                  <button
                    type="button"
                    className="btn-secondary h-10 px-3 flex items-center"
                    title="Upload Logo"
                    onClick={handleOpenImageUploadModal}
                  >
                    <ImagePlus size={16} />
                  </button>
                </div>
                <p className="text-xs text-text-muted-dark mt-1 mb-2">
                  Enter a URL to your product's logo or upload an image (JPG, PNG, GIF, SVG)
                </p>

                {logoPreview && (
                  <div className="mt-3 relative inline-flex">
                    <img
                      src={logoPreview}
                      alt="Logo preview"
                      className="h-16 w-16 object-contain bg-white rounded p-1 border border-border-dark"
                      onError={() => setLogoPreview(undefined)}
                    />
                    <button
                      type="button"
                      onClick={() => {
                        setValue('logo_url', '');
                        setLogoPreview(undefined);
                      }}
                      className="absolute -top-2 -right-2 bg-card-dark border border-border-dark rounded-full p-1 hover:bg-status-danger/20"
                    >
                      <X size={12} />
                    </button>
                  </div>
                )}

                {!logoPreview && (
                  <div 
                    className="mt-3 h-16 w-16 border border-dashed border-border-dark rounded flex items-center justify-center bg-background-hover cursor-pointer hover:bg-background-darker transition-colors"
                    onClick={handleOpenImageUploadModal}
                  >
                    <ImagePlus size={24} className="text-gray-500" />
                  </div>
                )}
              </div>
            </div>

            {/* Right column - Tags */}
            <div className="lg:col-span-2">
              {/* Dynamic Tags Section */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="form-label mb-0">
                    Product Tags
                  </label>
                  <button 
                    type="button"
                    onClick={() => setShowAddTagForm(true)}
                    className="text-xs bg-primary-700/30 text-primary-300 hover:bg-primary-700/50 px-2 py-1 rounded flex items-center"
                  >
                    <PlusCircle size={12} className="mr-1" />
                    Add Tag
                  </button>
                </div>
                
                {/* Existing Tags */}
                <div className="space-y-2 mb-3">
                  {tags.map((tag, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <div className="bg-gray-800/70 text-gray-300 px-3 py-2 rounded border border-gray-700 text-sm min-w-[120px]">
                        {tag.key}
                      </div>
                      <input
                        type="text"
                        value={tag.value}
                        onChange={(e) => handleTagValueChange(tag.key, e.target.value)}
                        className="form-input flex-grow"
                        placeholder={`Enter ${tag.key} value`}
                      />
                      <button
                        type="button"
                        onClick={() => handleRemoveTag(tag.key)}
                        className="p-2 bg-background-hover rounded hover:bg-status-danger/20 transition-colors"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  ))}
                </div>
                
                {/* Add Tag Form */}
                {showAddTagForm && (
                  <div className="bg-background-card p-3 border border-border-dark rounded-md mb-3">
                    <div className="flex items-center gap-2 mb-2">
                      <input
                        type="text"
                        value={newTagKey}
                        onChange={(e) => setNewTagKey(e.target.value)}
                        className="form-input flex-grow"
                        placeholder="Tag Key (e.g., category)"
                      />
                      <input
                        type="text"
                        value={newTagValue}
                        onChange={(e) => setNewTagValue(e.target.value)}
                        className="form-input flex-grow"
                        placeholder="Tag Value"
                      />
                    </div>
                    <div className="flex justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => setShowAddTagForm(false)}
                        className="btn-outline-secondary btn-sm"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={handleAddTag}
                        className="btn-primary btn-sm"
                        disabled={!newTagKey.trim()}
                      >
                        Add
                      </button>
                    </div>
                  </div>
                )}
                
                <p className="text-xs text-text-muted-dark mt-2">
                  Add tags to categorize and organize your products
                </p>
              </div>
            </div>
          </div>
        </FormSection>

        {/* Logger Configuration Section - Only shown when product type is logger */}
        {productType === 'logger' && (
          <FormSection 
            title="Logger Configuration" 
            leftIcon={<Database size={18} className="text-green-400" />}
            defaultOpen={true}
            id="logger-config"
            forceOpen={openSection === 'logger-config'}
          >
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div>
                <div className="mb-6">
                  <label htmlFor="product_type" className="form-label">
                    Product Type <span className="text-status-danger">*</span>
                    <Tooltip content="The type of product that generates the logs (e.g., Windows, Linux, Network)" />
                  </label>
                  <input
                    id="product_type"
                    name="product_type"
                    type="text"
                    className="form-input"
                    placeholder="e.g., Windows, Linux, Network"
                    value={loggerFormData.product_type}
                    onChange={handleLoggerFormChange}
                    required
                  />
                </div>

                <div className="mb-6">
                  <label htmlFor="log_type" className="form-label">
                    Log Type <span className="text-status-danger">*</span>
                    <Tooltip content="The type of logs this product generates (e.g., System, Application, Security)" />
                  </label>
                  <input
                    id="log_type"
                    name="log_type"
                    type="text"
                    className="form-input"
                    placeholder="e.g., System, Application, Security"
                    value={loggerFormData.log_type}
                    onChange={handleLoggerFormChange}
                    required
                  />
                </div>

                <div className="mb-6">
                  <label htmlFor="template_name" className="form-label">
                    Template Name
                    <Tooltip content="The template name used for log formatting" />
                  </label>
                  <input
                    id="template_name"
                    name="template_name"
                    type="text"
                    className="form-input"
                    placeholder="e.g., default_template"
                    value={loggerFormData.template_name}
                    onChange={handleLoggerFormChange}
                  />
                </div>

                <div className="mb-6">
                  <label htmlFor="filename" className="form-label">
                    File Name
                    <Tooltip content="The filename pattern for log files" />
                  </label>
                  <input
                    id="filename"
                    name="filename"
                    type="text"
                    className="form-input"
                    placeholder="e.g., %HOSTNAME%.log"
                    value={loggerFormData.filename}
                    onChange={handleLoggerFormChange}
                  />
                </div>
              </div>

              <div>
                <div className="mb-6">
                  <label htmlFor="queue_size" className="form-label">
                    Queue Size
                    <Tooltip content="The size of the message queue" />
                  </label>
                  <input
                    id="queue_size"
                    name="queue_size"
                    type="number"
                    className="form-input"
                    placeholder="1000"
                    value={loggerFormData.queue_size}
                    onChange={handleLoggerFormChange}
                  />
                </div>

                <div className="mb-6">
                  <label htmlFor="max_disk_space" className="form-label">
                    Max Disk Space
                    <Tooltip content="Maximum disk space in MB" />
                  </label>
                  <input
                    id="max_disk_space"
                    name="max_disk_space"
                    type="number"
                    className="form-input"
                    placeholder="100"
                    value={loggerFormData.max_disk_space}
                    onChange={handleLoggerFormChange}
                  />
                </div>

                <div className="mb-6">
                  <label htmlFor="worker_threads" className="form-label">
                    Worker Threads
                    <Tooltip content="Number of worker threads" />
                  </label>
                  <input
                    id="worker_threads"
                    name="worker_threads"
                    type="number"
                    className="form-input"
                    placeholder="4"
                    value={loggerFormData.worker_threads}
                    onChange={handleLoggerFormChange}
                  />
                </div>
              </div>
            </div>
          </FormSection>
        )}

        {/* Integration Fields Section */}
        {productType === 'saas' && (
        <FormSection 
          title="Integration Fields" 
          leftIcon={<Key size={18} className="text-secondary" />}
          badgeCount={regularFieldCount}
          defaultOpen={false}
          id="integration-fields"
          forceOpen={openSection === 'integration-fields'}
          actions={
            <div className="relative">
              <button
                type="button"
                className="btn-add flex items-center"
                onClick={(e) => {
                  e.stopPropagation();
                  handleAddRegularField();
                }}
              >
                <PlusCircle size={14} className="mr-1" />
                Add Field
              </button>
              {showAddOptions && (
                <div className="absolute right-0 mt-1 w-48 bg-card-dark rounded-md shadow-lg z-10 border border-border-dark overflow-hidden">
                  <div className="py-1">
                    <button
                      type="button"
                      className="w-full text-left px-4 py-2 text-sm hover:bg-background-hover transition-colors"
                      onClick={handleAddRegularField}
                    >
                      Add Regular Field
                    </button>
                    {!hasServicesField && (
                      <button
                        type="button"
                        className="w-full text-left px-4 py-2 text-sm hover:bg-background-hover transition-colors"
                        onClick={handleAddServicesField}
                      >
                        Add Services Field
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          }
        >
          {/* Field Form - Show for integration fields */}
          {showFieldForm && fieldType !== 'soar-mapping' && fieldType !== 'services' && (
            <div className="mb-4">
              <IntegrationFieldForm
                initialField={editingField}
                onSubmit={handleFieldSubmit}
                onCancel={handleCancelFieldForm}
                existingFieldKeys={existingFieldKeys}
                predefinedType={undefined}
                isServicesField={false}
              />
            </div>
          )}

          {/* Fields List - integration fields */}
          <div className="space-y-2">
            {integrationFields.filter(f => f.type !== 'soar-mapping' && f.field_key !== 'services').length === 0 && !(showFieldForm && fieldType !== 'soar-mapping' && fieldType !== 'services') ? (
              <div className="text-center py-8 text-text-muted-dark">
                <p>No integration fields defined yet.</p>
              </div>
            ) : (
              <>
                {integrationFields
                  .filter(f => f.type !== 'soar-mapping' && f.field_key !== 'services')
                  .map((field) => (
                    <IntegrationFieldItem
                      key={field.field_key}
                      field={field}
                      onEdit={handleEditField}
                      onDelete={handleDeleteField}
                    />
                  ))
                }
              </>
            )}
          </div>
        </FormSection>
        )}

        {/* Services Fields Section */}
        {productType === 'saas' && (
        <FormSection 
          title="Services Fields" 
          leftIcon={<Database size={18} className="text-primary" />}
          badgeCount={fieldGroups.configuration.length}
          defaultOpen={false}
          id="services-fields"
          forceOpen={openSection === 'services-fields'}
          actions={
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleAddServicesField();
              }}
              className="btn-add flex items-center"
            >
              <PlusCircle size={14} className="mr-1" />
              Add Services
            </button>
          }
        >
          {/* Field Form for services */}
          {showFieldForm && fieldType === 'services' && (
            <div className="mb-4">
              <IntegrationFieldForm
                initialField={editingField}
                onSubmit={handleFieldSubmit}
                onCancel={handleCancelFieldForm}
                existingFieldKeys={existingFieldKeys}
                predefinedType="multi-select"
                isServicesField={true}
              />
            </div>
          )}

          {/* Fields List - services fields */}
          <div className="space-y-2">
            {fieldGroups.configuration.length === 0 && !(showFieldForm && fieldType === 'services') ? (
              <div className="text-center py-8 text-text-muted-dark">
                <p>No services fields defined yet.</p>
              </div>
            ) : (
              <>
                {fieldGroups.configuration.map((field) => (
                  <IntegrationFieldItem
                    key={field.field_key}
                    field={field}
                    onEdit={handleEditField}
                    onDelete={handleDeleteField}
                  />
                ))}
              </>
            )}

            {!hasServicesField && (
              <button
                type="button"
                onClick={handleAddServicesField}
                className="mt-3 w-full text-left p-2 rounded border border-dashed border-gray-700 hover:border-primary/60 hover:bg-background-hover flex items-center justify-center"
              >
                <ListPlus size={16} className="mr-2 text-primary" />
                <span>Add Services List</span>
              </button>
            )}
          </div>
        </FormSection>
        )}
        
        {/* Other Fields Section */}
        {fieldGroups.other.length > 0 && (
          <FormSection 
            title="Other Fields" 
            leftIcon={<LinkIcon size={18} className="text-blue-400" />}
            badgeCount={fieldGroups.other.length}
            defaultOpen={false}
            id="other-fields"
            forceOpen={openSection === 'other-fields'}
          >
            <div className="space-y-2">
              {fieldGroups.other.map((field) => (
                <IntegrationFieldItem
                  key={field.field_key}
                  field={field}
                  onEdit={handleEditField}
                  onDelete={handleDeleteField}
                />
              ))}
            </div>
          </FormSection>
        )}

        {/* SOAR Field Mapping Section */}
        {productType === 'saas' && (
        <FormSection 
          title="SOAR Field Mapping" 
            leftIcon={<ArrowRight size={18} className="text-blue-400" />}
            badgeCount={Object.values(fieldsMap).filter(f => f.type === 'soar-mapping').length}
          defaultOpen={false}
          id="soar-mapping"
          forceOpen={openSection === 'soar-mapping'}
          actions={
            <button
              type="button"
                className="text-xs bg-primary-700/30 text-primary-300 hover:bg-primary-700/50 px-2 py-1 rounded flex items-center"
                onClick={handleAddSoarFieldMapping}
            >
                <PlusCircle size={12} className="mr-1" />
              Add Mapping
            </button>
          }
        >
          {/* SOAR Field Form */}
          {showFieldForm && fieldType === 'soar-mapping' && (
            <div className="mb-4">
              <IntegrationFieldForm
                initialField={editingField}
                onSubmit={handleFieldSubmit}
                onCancel={handleCancelFieldForm}
                existingFieldKeys={existingFieldKeys}
                predefinedType="soar-mapping"
                isServicesField={false}
              />
            </div>
          )}

          {/* SOAR Fields List */}
          <div className="space-y-2">
            {fieldGroups.mapping.length === 0 && !(showFieldForm && fieldType === 'soar-mapping') ? (
              <div className="text-center py-8 text-text-muted-dark">
                <p>No SOAR field mappings defined yet.</p>
              </div>
            ) : (
              <>
                {fieldGroups.mapping.map((field) => (
                  <IntegrationFieldItem
                    key={field.field_key}
                    field={field}
                    onEdit={handleEditField}
                    onDelete={handleDeleteField}
                  />
                ))}
              </>
            )}
          </div>
        </FormSection>
        )}

        {/* Preview section */}
        {integrationFields.length > 0 && (
          <FormSection 
            title="Integration Preview" 
            leftIcon={<Lock size={18} className="text-green-400" />}
            defaultOpen={false}
            id="preview"
            forceOpen={openSection === 'preview'}
          >
            <div className="bg-background-hover rounded-lg border border-border-dark p-4">
              <div className="flex items-center mb-4">
                {logoPreview ? (
                  <img
                    src={logoPreview}
                    alt="Product logo"
                    className="h-10 w-10 mr-3 bg-white rounded p-1"
                  />
                ) : (
                  <div className="h-10 w-10 mr-3 bg-background-hover rounded flex items-center justify-center">
                    <ImagePlus size={18} className="text-gray-500" />
                  </div>
                )}
                <div>
                  <h4 className="font-medium">{watch('name') || 'Product Name'}</h4>
                  {tags.map((tag, idx) => (
                    tag.value && (
                      <span key={idx} className="text-xs bg-primary-700/30 text-primary-300 px-2 py-0.5 rounded mr-1">
                        {tag.key}: {tag.value}
                      </span>
                    )
                  ))}
                </div>
              </div>

              <div className="space-y-3">
                {integrationFields.map((field) => (
                  <div key={field.field_key} className="mb-2">
                    <label className="block text-sm font-medium mb-1">
                      {field.label}
                      {field.is_required && (
                        <span className="text-status-danger ml-1">*</span>
                      )}
                    </label>
                    {field.type === 'multi-select' ? (
                      <div className="form-input bg-background-hover flex flex-wrap gap-1">
                        {field.options && field.options.length > 0 ? (
                          field.options.slice(0, 3).map((option, idx) => (
                            <span key={idx} className="text-primary-300 px-2 py-0.5 rounded text-xs border border-primary-700/40">
                              {option}
                            </span>
                          ))
                        ) : (
                          <span className="text-text-muted-dark text-sm">No options defined</span>
                        )}
                        {field.options && field.options.length > 3 && (
                          <span className="text-text-muted-dark text-xs">+{field.options.length - 3} more</span>
                        )}
                      </div>
                    ) : (
                      <input
                        type={field.type === 'secret' ? 'password' : field.type}
                        placeholder={`Enter ${field.label.toLowerCase()}`}
                        defaultValue={field.default_value}
                        className="form-input bg-background-dark"
                        disabled
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>
          </FormSection>
        )}

        <div className="flex justify-end space-x-3 mt-6 border-t border-border-dark pt-4">
          <button 
            type="button" 
            className="btn-subdued-secondary focus:outline-none focus:ring-1 focus:ring-offset-1 focus:ring-offset-background-dark relative z-20 cursor-pointer"
            disabled={isLoading || isSubmitting}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (isLoading || isSubmitting) return;
              window.history.back();
            }}
            tabIndex={0}
          >
            <span className="relative z-10">Cancel</span>
          </button>
          <button 
            type="submit" 
            className="btn-subdued-primary focus:outline-none focus:ring-1 focus:ring-offset-1 focus:ring-offset-background-dark relative z-20 cursor-pointer"
            disabled={isLoading || isSubmitting}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (isLoading || isSubmitting) return;
              
              // Add a transparent overlay to catch any clicks during submission
              const overlay = document.createElement('div');
              overlay.style.position = 'fixed';
              overlay.style.top = '0';
              overlay.style.left = '0';
              overlay.style.width = '100%';
              overlay.style.height = '100%';
              overlay.style.backgroundColor = 'transparent';
              overlay.style.zIndex = '9999';
              document.body.appendChild(overlay);
              
              // Process the form submission
              handleSubmit(submitHandler)(e);
              
              // Remove the overlay after a delay
              setTimeout(() => {
                document.body.removeChild(overlay);
              }, 1000);
            }}
            tabIndex={0}
          >
            <span className="relative z-10">
              {isLoading || isSubmitting ? (
                <span className="flex items-center">
                  <Loader2 size={16} className="animate-spin mr-2" />
                  Saving...
                </span>
              ) : (
                'Save Product'
              )}
            </span>
          </button>
        </div>
      </form>

      {/* Image Upload Modal */}
      <ImageUploadModal
        isOpen={showImageUploadModal}
        onClose={() => setShowImageUploadModal(false)}
        onImageSelected={handleImageSelected}
        currentUrl={watch('logo_url')}
      />
    </div>
  );
};

export default ProductForm;